/**
 * @file: useAutoApplyServiceSubjects.ts
 * @copyright: (c) 2019-2021 sichuan zhichetech co., ltd.
 */

import { useOrderFlowContext } from '@euler/app/flows/order/OrderFlowContext';
import { isOrderPropertyHidden } from '@euler/app/flows/order/pages/OrderScreen/functions/useOrderFormSections';
import { getFuelTypeByRawValue } from '@euler/functions/getFuelTypeByRawValue';
import { ServiceSubjectType, VehicleFuelType } from '@euler/model/enum';
import { usePersistFn } from '@euler/utils/hooks';
import { useEffect } from 'react';

export const useAutoApplyServiceSubjects = () => {
  const {
    mode,
    formController,
    inspectionTemplates,
    updateDetail,
    storeConfiguration,
  } = useOrderFlowContext();

  const { form } = formController;

  const apply = usePersistFn((fuelType: VehicleFuelType) => {
    if (
      isOrderPropertyHidden(
        storeConfiguration.app?.orderFlow?.fields,
        'serviceSubjects',
      )
    ) {
      return;
    }
    const templates = inspectionTemplates.filter(x => x.fuelType === fuelType);
    if (!templates.length) return;

    updateDetail(draft => {
      for (const template of templates) {
        const existing = draft.serviceSubjects?.find(
          x =>
            x.type === ServiceSubjectType.Inspection &&
            x.templateId === template.cid,
        );
        if (existing != null) continue; // ignore if already exists.

        if (!draft.serviceSubjects) {
          draft.serviceSubjects = [];
        }
        draft.serviceSubjects.push({
          type: ServiceSubjectType.Inspection,
          name: template.name,
          templateId: template.cid,
          assignedTo: {
            id: 0,
            name: '',
          },
        });
      }
    });
  });

  useEffect(() => {
    if (mode === 'update') return;
    if (form.licensePlateNo.value && form.vin.value && form.vehicleInfo.value) {
      // select inspection service subjects automatically if possible.
      const fuelType = getFuelTypeByRawValue(
        form.licensePlateNo.value,
        form.vehicleInfo.value.vehicleFuelType,
      );
      if (fuelType == null) return;
      apply(fuelType);
    }
  }, [
    form.licensePlateNo.value,
    form.vin.value,
    form.vehicleInfo.value,
    mode,
    apply,
  ]);
};
